import React, { useState, useEffect } from "react";
import { C, text } from "../theme";
import { Card, Btn, Notice } from "../components/ui";

const ACTION_COLORS = {
  CREATE: { bg: C.infoBg, text: C.info },
  SUBMIT: { bg: C.primaryBg, text: C.primary },
  APPROVE: { bg: C.successBg, text: C.success },
  REJECT: { bg: C.dangerBg, text: C.danger },
  UPDATE: { bg: C.warningBg, text: C.warning },
  UPLOAD: { bg: C.purpleBg, text: C.purple },
  XERO_PUSH: { bg: C.purpleBg, text: C.purple },
  MARK_PAID: { bg: C.successBg, text: C.success },
  CANCEL: { bg: C.dangerBg, text: C.danger },
};

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ docType: "", action: "", docId: "", dateFrom: "", dateTo: "" });
  const [notice, setNotice] = useState(null); // { kind: "success" | "error", text }

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (filters.docType) params.set("docType", filters.docType);
    if (filters.action) params.set("action", filters.action);
    if (filters.docId) params.set("docId", filters.docId);
    if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
    if (filters.dateTo) params.set("dateTo", filters.dateTo);

    Promise.all([
      fetch("/api/audit?" + params.toString()).then(r => r.json()),
      fetch("/api/audit/stats").then(r => r.json()),
    ]).then(([l, s]) => {
      setLogs(l);
      setStats(s);
    }).catch(console.error).finally(() => setLoading(false));
  };
  // Deliberate mount-only load: filters are applied explicitly via the Filter button (handleFilter).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const handleFilter = () => load();
  const clearFilters = () => {
    setFilters({ docType: "", action: "", docId: "", dateFrom: "", dateTo: "" });
    setTimeout(load, 100);
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.docType) params.set("docType", filters.docType);
      if (filters.docId) params.set("docId", filters.docId);
      if (filters.dateFrom) params.set("dateFrom", filters.dateFrom);
      if (filters.dateTo) params.set("dateTo", filters.dateTo);
      const res = await fetch("/api/audit/export?" + params.toString());
      const text = await res.text();
      const blob = new Blob([text], { type: "text/plain" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "SGNIC_Audit_Log_" + new Date().toISOString().slice(0, 10) + ".txt";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setNotice({ kind: "error", text: "Export failed: " + err.message });
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>Loading...</div>;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: text.h1, fontWeight: 700, color: C.gray1100, margin: 0 }}>Audit Trail</h1>
          <p style={{ fontSize: text.md, color: C.gray500, margin: "2px 0 0" }}>Complete log of all system actions</p>
        </div>
        <Btn onClick={handleExport}>📥 Export Log</Btn>
      </div>
      <Notice notice={notice} onDismiss={() => setNotice(null)} />

      {/* Stats */}
      {stats && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 20 }}>
          <div style={{ flex: 1, minWidth: 120, background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 4 }}>Total Events</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: C.gray1100 }}>{stats.total}</div>
          </div>
          <div style={{ flex: 1, minWidth: 120, background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 4 }}>Today</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: C.primary }}>{stats.todayCount}</div>
          </div>
          <div style={{ flex: 2, minWidth: 200, background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 8 }}>By Action</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {stats.byAction?.map(a => {
                const ac = ACTION_COLORS[a.action] || { bg: C.gray100, text: C.gray700 };
                return <span key={a.action} style={{ padding: "3px 10px", borderRadius: 12, fontSize: text.xs, fontWeight: 700, background: ac.bg, color: ac.text }}>{a.action} ({a.count})</span>;
              })}
            </div>
          </div>
          <div style={{ flex: 2, minWidth: 200, background: C.white, border: `1px solid ${C.gray200}`, borderRadius: 10, padding: "16px 20px" }}>
            <div style={{ fontSize: text.sm, color: C.gray500, marginBottom: 8 }}>By Document Type</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {stats.byDocType?.map(d => (
                <span key={d.docType} style={{ padding: "3px 10px", borderRadius: 12, fontSize: text.xs, fontWeight: 700, background: C.primaryBg, color: C.primary }}>{d.docType} ({d.count})</span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <Card style={{ marginBottom: 16, padding: 14 }}>
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <label style={{ fontSize: text.xs, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 4 }} htmlFor="audit-doc-type">Document Type</label>
            <select id="audit-doc-type" value={filters.docType} onChange={e => setFilters({ ...filters, docType: e.target.value })} style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", background: C.white }}>
              <option value="">All Types</option>
              {["AOR", "PO", "GRN", "INVOICE", "VIREMENT", "BUDGET", "SETTINGS"].map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: text.xs, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 4 }} htmlFor="audit-action">Action</label>
            <select id="audit-action" value={filters.action} onChange={e => setFilters({ ...filters, action: e.target.value })} style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", background: C.white }}>
              <option value="">All Actions</option>
              {["CREATE", "UPDATE", "SUBMIT", "APPROVE", "REJECT", "UPLOAD", "XERO_PUSH", "MARK_PAID", "CANCEL"].map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <div>
            <label style={{ fontSize: text.xs, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 4 }} htmlFor="audit-doc-id">Document ID</label>
            <input id="audit-doc-id" value={filters.docId} onChange={e => setFilters({ ...filters, docId: e.target.value })} placeholder="e.g. AOR-2026" style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ fontSize: text.xs, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 4 }} htmlFor="audit-date-from">From Date</label>
            <input id="audit-date-from" type="date" value={filters.dateFrom} onChange={e => setFilters({ ...filters, dateFrom: e.target.value })} style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit" }} />
          </div>
          <div>
            <label style={{ fontSize: text.xs, fontWeight: 600, color: C.gray600, display: "block", marginBottom: 4 }} htmlFor="audit-date-to">To Date</label>
            <input id="audit-date-to" type="date" value={filters.dateTo} onChange={e => setFilters({ ...filters, dateTo: e.target.value })} style={{ padding: "6px 10px", border: `1px solid ${C.gray300}`, borderRadius: 4, fontSize: text.md, fontFamily: "inherit" }} />
          </div>
          <Btn onClick={handleFilter}>Search</Btn>
          <Btn variant="outline" onClick={clearFilters}>Clear</Btn>
        </div>
      </Card>

      {/* Log entries */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "12px 20px", borderBottom: `1px solid ${C.gray200}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: text.md, fontWeight: 700 }}>{logs.length} events</span>
        </div>
        <div style={{ maxHeight: 600, overflowY: "auto" }}>
          {logs.map(log => {
            const ac = ACTION_COLORS[log.action] || { bg: C.gray100, text: C.gray700 };
            let meta = null;
            try { if (log.metadata) meta = JSON.parse(log.metadata); } catch (e) {}

            return (
              <div key={log.id} style={{ padding: "12px 20px", borderBottom: `1px solid ${C.gray100}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: ac.text, marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: text.micro, fontWeight: 700, background: ac.bg, color: ac.text }}>{log.action}</span>
                    <span style={{ padding: "2px 8px", borderRadius: 4, fontSize: text.micro, fontWeight: 700, background: C.primaryBg, color: C.primary }}>{log.docType}</span>
                    <span style={{ fontSize: text.md, fontWeight: 700, color: C.gray1100 }}>{log.docId}</span>
                  </div>
                  <div style={{ fontSize: text.sm, color: C.gray600, marginTop: 4 }}>
                    by <strong>{log.user?.displayName || "System"}</strong> • {new Date(log.createdAt).toLocaleString()}
                  </div>
                  {log.field && (
                    <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 4 }}>
                      Field: <strong>{log.field}</strong> | Old: {log.oldValue || "—"} → New: {log.newValue || "—"}
                    </div>
                  )}
                  {meta && (
                    <div style={{ fontSize: text.xs, color: C.gray500, marginTop: 4, background: C.gray50, padding: "4px 8px", borderRadius: 4, fontFamily: "monospace", overflowX: "auto" }}>
                      {Object.entries(meta).map(([k, v]) => (
                        <span key={k} style={{ marginRight: 12 }}>{k}: <strong>{typeof v === "object" ? JSON.stringify(v) : String(v)}</strong></span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          {logs.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: C.gray500 }}>No audit events found.</div>
          )}
        </div>
      </Card>
    </div>
  );
}